import {COMP_FIELDS} from '@/constants/comp';
import {INVE_FIELDS} from '@/constants/inve';
import {Contributor} from '@/model/src/values/dv-11';
import {ParticipantOrExSpouse} from '@/model/src/values/dv-12';
import {SourcePlans} from '@/model/src/values/dv-71';
import {Plans} from '@/model/src/values/dv-9';
import {ContractTypes} from '@/model/src/values/dv-93';
import {ArrayFilter, Condition} from '@/model/types';
import {Conditions} from './conditions';
import {and, or} from './conditions/utils';
import {Filters} from './filters';

type PlanToSourcePlanFieldMode = 'manual' | 'prefill';

export type SourcePlanRule = {
  plan: Plans;
  conditions: Condition[];
  sourcePlans: SourcePlans[];
  sort?: false;
};

const IS_NOT_SELECTED_ACCOUNT: ArrayFilter[] = [
  Filters.array.isDifferent(INVE_FIELDS.INVE43_existingAccountNo.asIndex(), {$ref: COMP_FIELDS.COMP1_accountNo}),
];

export const hasAtLeastOneExistingContractMatchingAccountProductType: Condition = or(
  ...Object.values(ContractTypes).map(type =>
    and(
      Conditions.fields.eq(COMP_FIELDS.COMP4_productType, type),
      Conditions.query.hasAnyRowInWhere(
        INVE_FIELDS.INVE44_existingAccountProductType.asQuery(),
        [type],
        IS_NOT_SELECTED_ACCOUNT,
      ),
    ),
  ),
);

export type SourcePlanRuleFields = {
  isPlan: (plan: Plans) => Condition;
  isSourceAccountContractType: (type: ContractTypes) => Condition;
  isSourceAccountContractTypeForFHSACondition: (type: ContractTypes) => Condition;
  isContractholder: Condition;
  isSpousal: Condition;
  isExSpouse: Condition;
  isParticipant: Condition;
  mode: PlanToSourcePlanFieldMode;
};

type PlanToSourcePlanOptions = {
  plan: Plans;
  conditions: Condition[];
  sourcePlans: {
    base: SourcePlans[];
    ciOnly: SourcePlans[];
  };
  isSourceAccountContractType: (type: ContractTypes) => Condition;
  mode: PlanToSourcePlanFieldMode;
};

const planToSourcePlans = ({
  plan,
  conditions,
  sourcePlans: {base: sourcePlans, ciOnly: ciOnlySourcePlans},
  isSourceAccountContractType,
  mode,
}: PlanToSourcePlanOptions): SourcePlanRule[] => {
  const hasCIOnlySourcePlans = ciOnlySourcePlans.length > 0;
  const rules: SourcePlanRule[] = [];

  if (mode === 'manual') {
    if (hasCIOnlySourcePlans) {
      rules.push({
        plan,
        conditions: [...conditions, isSourceAccountContractType(ContractTypes.CI)],
        sourcePlans: [...sourcePlans, ...ciOnlySourcePlans],
      });
    }
    rules.push({
      plan,
      conditions,
      sourcePlans,
    });
  } else {
    if (hasCIOnlySourcePlans) {
      rules.push({
        plan,
        conditions: [
          ...conditions,
          hasAtLeastOneExistingContractMatchingAccountProductType,
          isSourceAccountContractType(ContractTypes.CI),
        ],
        sourcePlans: [...sourcePlans, ...ciOnlySourcePlans],
      });
    }
    rules.push({
      plan,
      conditions: [...conditions, hasAtLeastOneExistingContractMatchingAccountProductType],
      sourcePlans: sourcePlans,
    });

    if (hasCIOnlySourcePlans) {
      rules.push({
        plan,
        conditions: [...conditions, isSourceAccountContractType(ContractTypes.CI)],
        sourcePlans: ciOnlySourcePlans,
      });
    }
  }

  return rules;
};

export const buildSourcePlanRules = ({
  isPlan,
  isSourceAccountContractType,
  isSourceAccountContractTypeForFHSACondition,
  isContractholder,
  isSpousal,
  isExSpouse,
  isParticipant,
  mode,
}: SourcePlanRuleFields): SourcePlanRule[] => [
  ...planToSourcePlans({
    plan: Plans.NRSP,
    conditions: [isPlan(Plans.NRSP)],
    sourcePlans: {base: [SourcePlans.TFSA, SourcePlans.NRSP_Non_Registered], ciOnly: [SourcePlans.FHSA]},
    isSourceAccountContractType,
    mode,
  }),
  ...planToSourcePlans({
    plan: Plans.RSP,
    conditions: [isPlan(Plans.RSP), isContractholder],
    sourcePlans: {
      base: [SourcePlans.TFSA, SourcePlans.NRSP_Non_Registered, SourcePlans.RRSP_Contributing_Contractholder],
      ciOnly: [SourcePlans.FHSA],
    },
    isSourceAccountContractType,
    mode,
  }),
  ...planToSourcePlans({
    plan: Plans.RSP,
    conditions: [isPlan(Plans.RSP), isSpousal],
    sourcePlans: {
      base: [SourcePlans.TFSA, SourcePlans.NRSP_Non_Registered, SourcePlans.RRSP_Contributing_Spouse],
      ciOnly: [],
    },
    isSourceAccountContractType,
    mode,
  }),
  ...planToSourcePlans({
    plan: Plans.RIF,
    conditions: [isPlan(Plans.RIF), isContractholder],
    sourcePlans: {
      base: [SourcePlans.RRSP_Contributing_Contractholder, SourcePlans.RRIF_Contributing_Contractholder],
      ciOnly: [SourcePlans.FHSA],
    },
    isSourceAccountContractType,
    mode,
  }),
  ...planToSourcePlans({
    plan: Plans.RIF,
    conditions: [isPlan(Plans.RIF), isSpousal],
    sourcePlans: {base: [SourcePlans.RRSP_Contributing_Spouse, SourcePlans.RRIF_Contributing_Spouse], ciOnly: []},
    isSourceAccountContractType,
    mode,
  }),
  {
    plan: Plans.LIRA,
    conditions: [isPlan(Plans.LIRA)],
    sourcePlans: [],
    sort: false,
  },
  ...planToSourcePlans({
    plan: Plans.LIF,
    conditions: [isPlan(Plans.LIF), isExSpouse],
    sourcePlans: {
      base: [SourcePlans.LIRA_Purchaser_Contractholder, SourcePlans.LIF_Purchaser_Contractholder],
      ciOnly: [],
    },
    isSourceAccountContractType,
    mode,
  }),
  ...planToSourcePlans({
    plan: Plans.LIF,
    conditions: [isPlan(Plans.LIF), isParticipant],
    sourcePlans: {base: [SourcePlans.LIRA_Member_Contractholder, SourcePlans.LIF_Member_Contractholder], ciOnly: []},
    isSourceAccountContractType,
    mode,
  }),
  ...planToSourcePlans({
    plan: Plans.TFSA,
    conditions: [isPlan(Plans.TFSA)],
    sourcePlans: {base: [SourcePlans.TFSA, SourcePlans.NRSP_Non_Registered], ciOnly: [SourcePlans.FHSA]},
    isSourceAccountContractType,
    mode,
  }),
  ...planToSourcePlans({
    plan: Plans.RLIF,
    conditions: [isPlan(Plans.RLIF), isExSpouse],
    sourcePlans: {
      base: [SourcePlans.RLIF_Purchaser_Contractholder, SourcePlans.RLSP_Purchaser_Contractholder],
      ciOnly: [],
    },
    isSourceAccountContractType,
    mode,
  }),
  ...planToSourcePlans({
    plan: Plans.RLIF,
    conditions: [isPlan(Plans.RLIF), isParticipant],
    sourcePlans: {base: [SourcePlans.RLIF_Member_Contractholder, SourcePlans.RLSP_Member_Contractholder], ciOnly: []},
    isSourceAccountContractType,
    mode,
  }),
  {
    plan: Plans.RLSP,
    conditions: [isPlan(Plans.RLSP), isExSpouse],
    sourcePlans: [],
    sort: false,
  },
  {
    plan: Plans.RLSP,
    conditions: [isPlan(Plans.RLSP), isParticipant],
    sourcePlans: [],
    sort: false,
  },
  ...planToSourcePlans({
    plan: Plans.FHSA,
    conditions: [isPlan(Plans.FHSA)],
    sourcePlans: {
      base: [
        SourcePlans.TFSA,
        SourcePlans.RRSP_Contributing_Contractholder,
        SourcePlans.RRIF_Contributing_Contractholder,
        SourcePlans.NRSP_Non_Registered,
      ],
      ciOnly: [SourcePlans.FHSA],
    },
    isSourceAccountContractType: isSourceAccountContractTypeForFHSACondition,
    mode,
  }),
];

type SourceAccountRequirement = {
  field: string;
  value: Contributor | ParticipantOrExSpouse;
};

type SourcePlanMapping = {
  plan: Plans;
  requirement?: SourceAccountRequirement;
};

const SPOUSAL_FLAG_FIELD = INVE_FIELDS.INVE40_existingAccountSpousalFlag.asIndex();
const PURCHASER_MEMBER_FIELD = INVE_FIELDS.INVE47_existingAccountPurchaserMemberContractHolder.asIndex();

const SOURCE_PLAN_MAPPINGS: Record<SourcePlans, SourcePlanMapping> = {
  [SourcePlans.NRSP_Non_Registered]: {
    plan: Plans.NRSP,
  },
  [SourcePlans.RRSP_Contributing_Contractholder]: {
    plan: Plans.RSP,
    requirement: {field: SPOUSAL_FLAG_FIELD, value: Contributor.CONTRACTHOLDER},
  },
  [SourcePlans.RRSP_Contributing_Spouse]: {
    plan: Plans.RSP,
    requirement: {field: SPOUSAL_FLAG_FIELD, value: Contributor.SPOUSAL},
  },
  [SourcePlans.RRIF_Contributing_Contractholder]: {
    plan: Plans.RIF,
    requirement: {field: SPOUSAL_FLAG_FIELD, value: Contributor.CONTRACTHOLDER},
  },
  [SourcePlans.RRIF_Contributing_Spouse]: {
    plan: Plans.RIF,
    requirement: {field: SPOUSAL_FLAG_FIELD, value: Contributor.SPOUSAL},
  },
  [SourcePlans.LIRA_Purchaser_Contractholder]: {
    plan: Plans.LIRA,
    requirement: {field: PURCHASER_MEMBER_FIELD, value: ParticipantOrExSpouse.EX_SPOUSE},
  },
  [SourcePlans.LIRA_Member_Contractholder]: {
    plan: Plans.LIRA,
    requirement: {field: PURCHASER_MEMBER_FIELD, value: ParticipantOrExSpouse.PARTICIPANT},
  },
  [SourcePlans.LIF_Purchaser_Contractholder]: {
    plan: Plans.LIF,
    requirement: {field: PURCHASER_MEMBER_FIELD, value: ParticipantOrExSpouse.EX_SPOUSE},
  },
  [SourcePlans.LIF_Member_Contractholder]: {
    plan: Plans.LIF,
    requirement: {field: PURCHASER_MEMBER_FIELD, value: ParticipantOrExSpouse.PARTICIPANT},
  },
  [SourcePlans.TFSA]: {
    plan: Plans.TFSA,
  },
  [SourcePlans.RLIF_Purchaser_Contractholder]: {
    plan: Plans.RLIF,
    requirement: {field: PURCHASER_MEMBER_FIELD, value: ParticipantOrExSpouse.EX_SPOUSE},
  },
  [SourcePlans.RLIF_Member_Contractholder]: {
    plan: Plans.RLIF,
    requirement: {field: PURCHASER_MEMBER_FIELD, value: ParticipantOrExSpouse.PARTICIPANT},
  },
  [SourcePlans.RLSP_Purchaser_Contractholder]: {
    plan: Plans.RLSP,
    requirement: {field: PURCHASER_MEMBER_FIELD, value: ParticipantOrExSpouse.EX_SPOUSE},
  },
  [SourcePlans.RLSP_Member_Contractholder]: {
    plan: Plans.RLSP,
    requirement: {field: PURCHASER_MEMBER_FIELD, value: ParticipantOrExSpouse.PARTICIPANT},
  },
  [SourcePlans.FHSA]: {
    plan: Plans.FHSA,
  },
};

type SourcePlanGroup = {
  plans: Plans[];
  requirement?: SourceAccountRequirement;
};

const groupSourcePlansByRequirement = (rule: SourcePlanRule): SourcePlanGroup[] => {
  const groups = new Map<string, SourcePlanGroup>();

  rule.sourcePlans.forEach(sourcePlan => {
    const {plan, requirement} = SOURCE_PLAN_MAPPINGS[sourcePlan];
    const key = requirement ? `${requirement.field}=${requirement.value}` : '';
    const group = groups.get(key) ?? {plans: [], ...(requirement ? {requirement} : {})};
    if (!group.plans.includes(plan)) group.plans.push(plan);
    groups.set(key, group);
  });

  return [...groups.values()];
};

export const isAllowedSourcePlanForRule = (rule: SourcePlanRule): Condition =>
  or(
    ...groupSourcePlansByRequirement(rule).map(({plans, requirement}) =>
      and(
        Conditions.fields.isIn(INVE_FIELDS.INVE45_existingAccountPlan.asIndex(), plans),
        ...(requirement ? [Conditions.fields.eq(requirement.field, requirement.value)] : []),
      ),
    ),
  );

export const hasAnyExistingAccountAllowedForRule = (rule: SourcePlanRule, where: ArrayFilter[] = []): Condition =>
  or(
    ...groupSourcePlansByRequirement(rule).map(({plans, requirement}) =>
      Conditions.query.hasAnyRowInWhere(INVE_FIELDS.INVE45_existingAccountPlan.asQuery(), plans, [
        ...where,
        ...(requirement ? [Filters.array.isEqual(requirement.field, {$ref: requirement.value})] : []),
      ]),
    ),
  );
